/**
 * La trayectoria del saldo.
 *
 * ---------------------------------------------------------------------------
 *  El error fácil, y por qué se hace al revés
 * ---------------------------------------------------------------------------
 *
 * Lo natural sería sumar los flujos de los meses que se ven y llamarlo «saldo
 * acumulado». **Eso está mal**, y de una forma que engaña:
 *
 *  · El resultado no es el saldo, es **cuánto ha cambiado** en ese tramo.
 *  · Si la aplicación no existía antes, la línea empieza en cero y sube como si el
 *    dinero hubiera aparecido de la nada.
 *  · Y no coincide con el saldo que el usuario ve en su cuenta, así que parece que la
 *    aplicación se equivoca.
 *
 * Lo que se hace aquí es lo contrario: **se parte del dinero que hay hoy y se va hacia
 * atrás**, restando lo que se movió cada mes. Así la línea termina exactamente en la
 * cifra real, y el pasado que se dibuja es coherente con el presente.
 *
 * ---------------------------------------------------------------------------
 *  La sutileza que hay que entender bien
 * ---------------------------------------------------------------------------
 *
 * Cada punto es el saldo **al final** de su mes. Eso significa que el punto del primer
 * mes ya incluye lo que se movió ese mes, y por tanto **la diferencia entre el primer
 * punto y hoy NO es la suma de todos los flujos**: es la suma de todos menos el del
 * primer mes.
 *
 * Eso es fácil de equivocar, y por eso la función devuelve también
 * `startingBalance` —el saldo que había **antes** del primer mes— y `change`, que es
 * lo que de verdad ha cambiado el saldo a lo largo de toda la ventana. Así el que
 * dibuja el gráfico no tiene que deducirlo, y el que lo lee ve la cifra correcta.
 */

/** El flujo de un mes, tal como lo da el servidor. */
export interface MonthNet {
  monthKey: string;
  net: number;
}

export interface TrajectoryPoint {
  monthKey: string;
  /** El saldo al **final** de ese mes. */
  balance: number;
}

export interface Trajectory {
  /** El saldo al final de cada mes, del más antiguo al más reciente. */
  points: TrajectoryPoint[];
  /** El saldo que había **antes** del primer mes de la ventana. */
  startingBalance: number;
  /** Lo que ha cambiado el saldo a lo largo de toda la ventana. */
  change: number;
  /** El saldo de hoy: el mismo que el último punto. */
  currentBalance: number;
}

/**
 * Calcula el saldo al final de cada mes, desde el más antiguo hasta el más reciente.
 *
 * @param points Los meses, en orden cronológico, con su flujo neto.
 * @param currentBalance El dinero que hay **hoy** en las cuentas.
 */
export function balanceTrajectory(
  points: readonly MonthNet[],
  currentBalance: number,
): Trajectory {
  if (points.length === 0) {
    return { points: [], startingBalance: currentBalance, change: 0, currentBalance };
  }

  // Se recorre de atrás hacia delante: el último mes termina en el saldo de hoy, y el
  // anterior termina en ese saldo menos lo que se movió en el último mes.
  const result: TrajectoryPoint[] = [];
  let running = currentBalance;

  for (let index = points.length - 1; index >= 0; index -= 1) {
    const point = points[index]!;
    result.push({ monthKey: point.monthKey, balance: running });
    running -= point.net;
  }

  result.reverse();

  // Después del recorrido, `running` es el saldo que había antes del primer mes.
  return {
    points: result,
    startingBalance: running,
    // El cambio de toda la ventana se mide contra ese saldo anterior, no contra el
    // primer punto: el primer punto ya incluye el movimiento de su propio mes.
    change: currentBalance - running,
    currentBalance,
  };
}
